/**
 * DesignSystemCard — escolha do Design System do projeto (Onda 1 do plano de qualidade).
 *
 * A paleta fixa da Nava saiu do Design Studio. O cliente pode:
 *   - "Deixar a IA propor" (padrão): a IA escolhe paleta e tipografia pelo domínio do
 *     cliente; o resultado aparece aqui para aprovar ou substituir;
 *   - "Usar o meu": colar/enviar tokens (W3C Design Tokens, CSS com variáveis, Style
 *     Dictionary, Tokens Studio, Figma Variables, Tailwind), pré-visualizar (avisos +
 *     contraste + amostras) e ativar;
 *   - "Extrair das telas atuais" (Onda 5): lê os tokens do HTML das telas já geradas e
 *     devolve um rascunho para revisar e ativar.
 * Com DS ativo, "Re-tematizar as telas" aplica os tokens às telas atuais sem regerá-las.
 *
 * Projeto existente (brownfield, 2026-10-05): a opção PRINCIPAL é o Design System extraído
 * do repositório indexado (`DesignSystemDoRepositorio`, que some sozinho quando não se
 * aplica); as três opções acima continuam como alternativa.
 *
 * As amostras usam os VALORES do DS (hex do token em style inline); a paleta `C` só
 * pinta o cromo do cartão.
 *
 * a11y: opções em role="radiogroup" com rádios nativos, rótulos em todos os inputs,
 * avisos e erros em aria-live.
 */
import { useId, useState } from 'react';
import { C } from '@/theme/palette';
import { Btn, ConfirmDialog } from '@/components/ui';
import {
  useDesignSystem, usePreviewDesignSystem, useActivateDesignSystem, useDeleteDesignSystem,
  useExtractDesignSystem, useRetematizar,
} from '@/api/designSystem';
import { DesignSystemDoRepositorio } from '@/components/DesignSystemDoRepositorio';

const ORIGEM_LABEL = {
  repository: 'Extraído do repositório',
  injected: 'Enviado por você',
  proposed: 'Proposto pela IA',
  extracted: 'Extraído das telas',
};

const MODO_LABEL = {
  enforce: 'Obrigatório (enforce)',
  guide: 'Orientativo (guide)',
};

/** Mensagem de erro da API em pt-BR (usa o `detail` quando existir). */
export function mensagemDeErro(err) {
  if (!err) return '';
  const detail = err.detail ?? err.problemBody?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length) {
    return detail.map((d) => (typeof d === 'string' ? d : d?.msg)).filter(Boolean).join('; ');
  }
  if (err.status === 403) return 'Você não tem permissão para alterar o Design System deste projeto.';
  if (err.status >= 500) return 'Falha no servidor ao processar o Design System. Tente de novo.';
  return 'Não foi possível concluir a operação com o Design System.';
}

function formatarRazao(r) {
  const n = Number(r);
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}:1`;
}

// ─── Blocos visuais ───────────────────────────────────────────────────────────

/** `#RRGGBBAA` (papel `overlay`) → opacidade em % inteiro; `null` para cor opaca. */
function opacidade(hex) {
  const m = /^#[0-9a-f]{6}([0-9a-f]{2})$/i.exec(String(hex ?? ''));
  return m ? Math.round((parseInt(m[1], 16) / 255) * 100) : null;
}

function Amostras({ canonical }) {
  // Tema escuro (grupo `color-dark`): derivado pela plataforma quando o escopo o pede.
  // Aparece ao lado do claro para o dono ver as duas paletas antes de gerar as telas.
  const temas = [
    ['Amostras de cor', '', Object.entries(canonical?.color ?? {})],
    ['Amostras de cor do tema escuro', 'escuro-', Object.entries(canonical?.['color-dark'] ?? {})],
  ];
  const familia = canonical?.typography?.['font-family'];
  return (
    <div>
      {temas.map(([rotulo, prefixo, cores]) => cores.length > 0 && (
        <div key={rotulo}>
        {prefixo && (
          <p data-testid="titulo-tema-escuro" style={{ margin: '12px 0 0', fontSize: 12, color: C.neutralDim }}>
            Tema escuro — derivado do tema claro, com o contraste conferido
          </p>
        )}
        <ul aria-label={rotulo} style={{ listStyle: 'none', padding: 0, margin: '10px 0 0',
          display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {cores.map(([nome, hex]) => (
            <li key={nome} data-testid={`amostra-${prefixo}${nome}`}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                borderRadius: 8, border: `1px solid ${C.blackBorder}`, background: C.blackElev2 }}>
              <span aria-hidden="true" data-testid={`quadrado-${prefixo}${nome}`}
                style={{ width: 22, height: 22, borderRadius: 5,
                  // cor com alfa (overlay): xadrez por baixo para a transparência aparecer
                  background: opacidade(hex) == null ? hex
                    : `linear-gradient(${hex}, ${hex}), repeating-conic-gradient(#9CA3AF 0 25%, #F3F4F6 0 50%) 0 0 / 8px 8px`,
                  border: `1px solid ${C.blackBorder}`, flexShrink: 0 }} />
              <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
                <span style={{ fontSize: 12, color: C.neutral }}>{nome}</span>
                <span style={{ fontSize: 11, fontFamily: 'monospace', color: C.neutralDim }}>{hex}</span>
                {opacidade(hex) != null && (
                  <span style={{ fontSize: 11, color: C.neutralDim }}>
                    {`opacidade ${opacidade(hex).toLocaleString('pt-BR')}%`}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
        </div>
      ))}
      {familia && (
        <p style={{ margin: '12px 0 0', fontSize: 13, color: C.neutralDim }}>
          Tipografia:{' '}
          <span style={{ fontFamily: familia, color: C.neutral, fontSize: 15 }}>{familia}</span>
        </p>
      )}
    </div>
  );
}

function Avisos({ avisos }) {
  if (!avisos?.length) return null;
  return (
    <div role="status" aria-live="polite"
      style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8,
        border: `1px solid ${C.warn}66`, background: 'rgba(255,176,32,0.08)' }}>
      <strong style={{ color: C.warn, fontSize: 13 }}>Avisos</strong>
      <ul style={{ margin: '6px 0 0', paddingLeft: 18, color: C.neutral, fontSize: 12.5 }}>
        {avisos.map((a, i) => <li key={i}>{a}</li>)}
      </ul>
    </div>
  );
}

function TabelaContraste({ contraste }) {
  if (!contraste?.length) return null;
  const th = { textAlign: 'left', padding: '6px 10px', color: C.neutralMuted, fontWeight: 600,
    borderBottom: `1px solid ${C.blackBorder}` };
  const td = { padding: '6px 10px', borderBottom: `1px solid ${C.blackBorder}` };
  return (
    <table style={{ marginTop: 12, borderCollapse: 'collapse', fontSize: 12.5, color: C.neutral,
      width: '100%' }}>
      <caption style={{ textAlign: 'left', color: C.neutralDim, fontSize: 13, marginBottom: 6 }}>
        Contraste (WCAG AA)
      </caption>
      <thead>
        <tr>
          <th scope="col" style={th}>Par</th>
          <th scope="col" style={th}>Razão</th>
          <th scope="col" style={th}>Resultado</th>
        </tr>
      </thead>
      <tbody>
        {contraste.map((c, i) => (
          <tr key={`${c.par}-${i}`}>
            <td style={td}>
              <span aria-hidden="true" style={{ display: 'inline-block', padding: '1px 6px',
                marginRight: 8, borderRadius: 4, background: c.fundo, color: c.frente,
                fontSize: 11, fontWeight: 700 }}>Aa</span>
              {c.par}
            </td>
            <td style={{ ...td, fontFamily: 'monospace' }}>{formatarRazao(c.razao)}</td>
            <td style={{ ...td, color: c.ok ? C.ok : C.bad, fontWeight: 700 }}>
              {c.ok ? 'ok' : 'reprovado'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SeletorModo({ id, value, onChange }) {
  return (
    <label htmlFor={id} style={{ fontSize: 13 }}>
      Modo{' '}
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)}
        style={{ background: C.black, color: C.neutral, border: `1px solid ${C.blackBorder}`,
          borderRadius: 6, padding: '5px 8px', fontSize: 13 }}>
        <option value="enforce">{MODO_LABEL.enforce}</option>
        <option value="guide">{MODO_LABEL.guide}</option>
      </select>
    </label>
  );
}

function PreviewRascunho({ rascunho, onAtivar, ativando }) {
  if (!rascunho) return null;
  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.blackBorder}` }}>
      <strong style={{ fontSize: 13 }}>Pré-visualização (rascunho)</strong>
      {rascunho.origin && (
        <span style={{ marginLeft: 8, fontSize: 12, color: C.neutralDim }}>
          {ORIGEM_LABEL[rascunho.origin] ?? rascunho.origin}
        </span>
      )}
      <Avisos avisos={rascunho.avisos} />
      <TabelaContraste contraste={rascunho.contraste} />
      <Amostras canonical={rascunho.canonical} />
      <div style={{ marginTop: 14 }}>
        <Btn size="sm" onClick={() => onAtivar(rascunho)} disabled={ativando || !rascunho.id}>
          {ativando ? 'Ativando…' : 'Ativar este Design System'}
        </Btn>
      </div>
    </div>
  );
}

function ResultadoRetematizar({ resultado }) {
  if (!resultado) return null;
  const telas = Number(resultado.telas ?? 0);
  const sem = Number(resultado.semVariaveis ?? 0);
  return (
    <div role="status" aria-live="polite" style={{ marginTop: 12, fontSize: 13 }}>
      <p style={{ margin: 0, color: C.neutral }}>
        {telas.toLocaleString('pt-BR')} tela(s) atualizada(s)
      </p>
      {sem > 0 && (
        <p style={{ margin: '6px 0 0', color: C.warn }}>
          {sem.toLocaleString('pt-BR')} tela(s) não usam as variáveis do Design System e não
          mudaram — regere-as para aplicar.
        </p>
      )}
    </div>
  );
}

// ─── Cartão ───────────────────────────────────────────────────────────────────

export function DesignSystemCard({ projectId, brownfield = false }) {
  const uid = useId();
  const { data: ativo, isLoading, error: erroGet } = useDesignSystem(projectId);
  const preview = usePreviewDesignSystem(projectId);
  const activate = useActivateDesignSystem(projectId);
  const remove = useDeleteDesignSystem(projectId);
  const extract = useExtractDesignSystem(projectId);
  const retematizar = useRetematizar(projectId);

  const [substituindo, setSubstituindo] = useState(false);
  const [opcao, setOpcao] = useState('ia');
  const [conteudo, setConteudo] = useState('');
  const [modo, setModo] = useState('enforce');
  const [erroArquivo, setErroArquivo] = useState('');
  const [confirmarRemocao, setConfirmarRemocao] = useState(false);
  const [modoExtracao, setModoExtracao] = useState('enforce');
  const [confirmarRetema, setConfirmarRetema] = useState(false);

  if (!projectId) return null;

  const mostrarEscolha = !ativo || substituindo;
  const doRepositorio = ativo?.origin === 'repository';

  const abrirSubstituir = () => {
    setSubstituindo(true);
    setOpcao('meu');
  };

  const cancelarSubstituir = () => {
    setSubstituindo(false);
    setOpcao('ia');
    preview.reset?.();
    extract.reset?.();
  };

  const lerArquivo = async (e) => {
    setErroArquivo('');
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const texto = typeof file.text === 'function'
        ? await file.text()
        : await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(String(r.result ?? ''));
          r.onerror = () => reject(r.error);
          r.readAsText(file);
        });
      setConteudo(texto);
    } catch {
      setErroArquivo('Não foi possível ler o arquivo. Cole o conteúdo no campo acima.');
    }
  };

  const preVisualizar = () => {
    preview.mutate({ content: conteudo, sourceKind: 'auto', mode: modo });
  };

  const extrair = () => {
    extract.mutate({ mode: modoExtracao });
  };

  const ativar = (rascunho) => {
    if (!rascunho?.id) return;
    activate.mutate(rascunho.id, {
      onSuccess: () => {
        setSubstituindo(false);
        setConteudo('');
        setOpcao('ia');
        preview.reset?.();
        extract.reset?.();
      },
    });
  };

  const aplicarRetema = () => {
    retematizar.mutate(undefined, { onSettled: () => setConfirmarRetema(false) });
  };

  const remover = () => {
    if (!ativo?.id) return;
    remove.mutate(ativo.id, {
      onSettled: () => setConfirmarRemocao(false),
      onSuccess: () => { setSubstituindo(false); setOpcao('ia'); },
    });
  };

  const cartao = {
    margin: '0 0 16px', padding: 16, borderRadius: 12,
    border: `1px solid ${C.blackBorder}`, background: C.blackElev, color: C.neutral,
  };
  const rotuloOpcao = (ativa, desabilitada) => ({
    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8,
    border: `1px solid ${ativa ? C.cyber : C.blackBorder}`,
    background: ativa ? 'rgba(1,230,228,0.08)' : 'transparent',
    color: desabilitada ? C.neutralMuted : C.neutral,
    cursor: desabilitada ? 'not-allowed' : 'pointer', fontSize: 13,
  });

  const erroAtual = activate.error || remove.error || retematizar.error;

  return (
    <section aria-labelledby={`${uid}-titulo`} style={cartao}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h3 id={`${uid}-titulo`} style={{ margin: 0, fontSize: 15 }}>Design System</h3>
        {ativo && (
          <span style={{ fontFamily: 'monospace', fontSize: 11, padding: '3px 9px', borderRadius: 6,
            background: 'rgba(1,230,228,0.12)', color: C.cyber }}>
            ativo
          </span>
        )}
      </div>

      {isLoading && (
        <p style={{ margin: '10px 0 0', fontSize: 13, color: C.neutralDim }}>Carregando Design System…</p>
      )}

      {erroGet && (
        <p role="alert" style={{ margin: '10px 0 0', fontSize: 13, color: C.bad }}>
          {mensagemDeErro(erroGet)}
        </p>
      )}

      {/* Projeto existente: o Design System do repositório é a opção principal */}
      {brownfield && <DesignSystemDoRepositorio projectId={projectId} />}

      {brownfield && !ativo && !isLoading && (
        <p style={{ margin: '10px 0 0', fontSize: 13, color: C.neutralDim, lineHeight: 1.5 }}>
          Projeto existente: sem Design System enviado, as telas reproduzem o visual do front-end atual.
        </p>
      )}

      {/* ── DS ativo ── */}
      {ativo && !substituindo && (
        <div style={{ marginTop: 10 }}>
          <p style={{ margin: 0, fontSize: 13, color: C.neutralDim }}>
            <strong style={{ color: C.neutral }}>{ORIGEM_LABEL[ativo.origin] ?? ativo.origin}</strong>
            {' · '}versão {ativo.version}
            {' · '}modo {MODO_LABEL[ativo.mode] ?? ativo.mode}
          </p>
          <Amostras canonical={ativo.canonical} />
          <Avisos avisos={ativo.avisos} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
            <Btn size="sm" onClick={() => setConfirmarRetema(true)} disabled={retematizar.isPending}>
              {retematizar.isPending ? 'Re-tematizando…' : 'Re-tematizar as telas'}
            </Btn>
            <Btn variant="outline" size="sm" onClick={abrirSubstituir}>
              {doRepositorio ? 'Usar outra opção' : 'Substituir'}
            </Btn>
            <Btn variant="danger" size="sm" onClick={() => setConfirmarRemocao(true)}
              disabled={remove.isPending}>
              {doRepositorio ? 'Deixar de usar' : 'Remover'}
            </Btn>
          </div>
          <ResultadoRetematizar resultado={retematizar.data} />
        </div>
      )}

      {/* ── Escolha / substituição ── */}
      {mostrarEscolha && !isLoading && (
        <div style={{ marginTop: 12 }}>
          {brownfield && (
            <p style={{ margin: '0 0 8px', fontSize: 13, color: C.neutralDim }}>
              Outras opções (caso queira mudar):
            </p>
          )}
          <div role="radiogroup" aria-label="Origem do Design System"
            style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <label style={rotuloOpcao(opcao === 'ia', false)}>
              <input type="radio" name={`${uid}-opcao`} value="ia"
                checked={opcao === 'ia'} onChange={() => setOpcao('ia')} />
              Deixar a IA propor
            </label>
            <label style={rotuloOpcao(opcao === 'meu', false)}>
              <input type="radio" name={`${uid}-opcao`} value="meu"
                checked={opcao === 'meu'} onChange={() => setOpcao('meu')} />
              Usar o meu
            </label>
            <label style={rotuloOpcao(opcao === 'extrair', false)}>
              <input type="radio" name={`${uid}-opcao`} value="extrair"
                checked={opcao === 'extrair'} onChange={() => setOpcao('extrair')} />
              Extrair das telas atuais
            </label>
          </div>

          {opcao === 'ia' && (
            <p style={{ margin: '12px 0 0', fontSize: 13, color: C.neutralDim, lineHeight: 1.5, maxWidth: '78ch' }}>
              A IA escolhe a paleta e a tipografia de acordo com o domínio do cliente. O resultado
              aparece aqui para você aprovar ou substituir pelo seu.
            </p>
          )}

          {opcao === 'meu' && (
            <div style={{ marginTop: 12 }}>
              <label htmlFor={`${uid}-conteudo`} style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
                Tokens do Design System
              </label>
              <p id={`${uid}-formatos`} style={{ margin: '0 0 8px', fontSize: 12.5, color: C.neutralDim, lineHeight: 1.5 }}>
                Formatos aceitos: W3C Design Tokens (JSON), CSS com variáveis (<code>--x: valor</code>),
                Style Dictionary, Tokens Studio, Figma Variables (JSON exportado), Tailwind (JSON do
                theme ou <code>@theme</code> v4). O formato é detectado automaticamente.
              </p>
              <textarea id={`${uid}-conteudo`} value={conteudo} rows={8}
                aria-describedby={`${uid}-formatos`}
                onChange={(e) => setConteudo(e.target.value)}
                placeholder={'{ "color": { "primary": { "$value": "#1E88E5" } } }\nou\n:root { --color-primary: #1E88E5; }'}
                style={{ width: '100%', boxSizing: 'border-box', background: C.black, color: C.neutral,
                  border: `1px solid ${C.blackBorder}`, borderRadius: 8, padding: '9px 12px',
                  fontSize: 12.5, fontFamily: 'monospace', resize: 'vertical' }} />

              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
                <label htmlFor={`${uid}-arquivo`} style={{ fontSize: 13 }}>
                  Ou envie um arquivo (.json ou .css){' '}
                  <input id={`${uid}-arquivo`} type="file" accept=".json,.css,application/json,text/css"
                    onChange={lerArquivo} style={{ color: C.neutralDim, fontSize: 12 }} />
                </label>
                <SeletorModo id={`${uid}-modo`} value={modo} onChange={setModo} />
              </div>
              {erroArquivo && (
                <p role="alert" style={{ margin: '8px 0 0', fontSize: 12.5, color: C.bad }}>{erroArquivo}</p>
              )}

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                <Btn size="sm" onClick={preVisualizar}
                  disabled={!conteudo.trim() || preview.isPending}>
                  {preview.isPending ? 'Pré-visualizando…' : 'Pré-visualizar'}
                </Btn>
                {substituindo && (
                  <Btn variant="ghost" size="sm" onClick={cancelarSubstituir}>Cancelar</Btn>
                )}
              </div>

              {preview.error && (
                <p role="alert" style={{ margin: '10px 0 0', fontSize: 13, color: C.bad }}>
                  {mensagemDeErro(preview.error)}
                </p>
              )}

              <PreviewRascunho rascunho={preview.data} onAtivar={ativar} ativando={activate.isPending} />
            </div>
          )}

          {opcao === 'extrair' && (
            <div style={{ marginTop: 12 }}>
              <p style={{ margin: 0, fontSize: 13, color: C.neutralDim, lineHeight: 1.5, maxWidth: '78ch' }}>
                Lê as cores, fontes, raios e espaçamentos das telas já geradas e propõe os tokens.
                Revise antes de ativar.
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
                <SeletorModo id={`${uid}-modo-extracao`} value={modoExtracao} onChange={setModoExtracao} />
                <Btn size="sm" onClick={extrair} disabled={extract.isPending}>
                  {extract.isPending ? 'Extraindo…' : 'Extrair'}
                </Btn>
                {substituindo && (
                  <Btn variant="ghost" size="sm" onClick={cancelarSubstituir}>Cancelar</Btn>
                )}
              </div>

              {extract.error && (
                <p role="alert" style={{ margin: '10px 0 0', fontSize: 13, color: C.bad }}>
                  {mensagemDeErro(extract.error)}
                </p>
              )}

              <PreviewRascunho rascunho={extract.data} onAtivar={ativar} ativando={activate.isPending} />
            </div>
          )}
        </div>
      )}

      {erroAtual && (
        <p role="alert" style={{ margin: '10px 0 0', fontSize: 13, color: C.bad }}>
          {mensagemDeErro(erroAtual)}
        </p>
      )}

      <ConfirmDialog
        open={confirmarRemocao}
        title={doRepositorio ? 'Deixar de usar o Design System do repositório?' : 'Remover o Design System?'}
        message={doRepositorio
          ? 'As próximas telas deixam de usar estes tokens e voltam a reproduzir o visual a partir dos trechos do front-end. A extração fica guardada: dá para voltar a usá-la ou extrair de novo.'
          : 'As próximas telas deixam de usar estes tokens. Sem Design System, a IA volta a propor a paleta pelo domínio do cliente.'}
        confirmLabel={doRepositorio ? 'Deixar de usar' : 'Remover'}
        tone="danger"
        busy={remove.isPending}
        onConfirm={remover}
        onCancel={() => setConfirmarRemocao(false)}
      />

      <ConfirmDialog
        open={confirmarRetema}
        title="Re-tematizar as telas?"
        message="Aplica este Design System às telas atuais sem regerá-las."
        confirmLabel="Re-tematizar"
        busy={retematizar.isPending}
        onConfirm={aplicarRetema}
        onCancel={() => setConfirmarRetema(false)}
      />
    </section>
  );
}

export default DesignSystemCard;
